import type {
  Event,
  EventCtaMode,
  EventDropPoint,
  EventFormOptions,
  EventPickupRequest,
  PickupRequestStatus,
  EventPublishState,
  EventRegistration,
  EventStatus,
  EventVisibility,
  RegistrationMemberType,
  RegistrationPaymentStatus,
  RegistrationSummary,
} from '../types';
import { ApiError, apiFetch } from './api';
import { adminPinDelete } from './admin-pin-client';
import { apiUrl } from './api-base';

interface EventsResponse {
  ok: boolean;
  events: Event[];
}

interface EventResponse {
  ok: boolean;
  event: Event;
}

export interface EventWritePayload {
  title: string;
  description: string;
  date: string;
  location: string;
  /** http(s) venue map link, or null when the venue name is plain text. */
  venueMapUrl?: string | null;
  image?: string | null;
  cardImage?: string | null;
  status: EventStatus;
  ctaMode: EventCtaMode;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  coordinator?: string | null;
  specialMention?: string | null;
  participantCountOverride?: number | null;
  detailsUrl?: string | null;
  detailsLabel?: string | null;
  galleryAlbumId?: string | null;
  slug?: string;
  regPrefix?: string;
  publishState?: EventPublishState;
  visibility?: EventVisibility;
  registrationOpen?: boolean;
  registrationNotRequired?: boolean;
  registrationComingSoon?: boolean;
  deadlineAt?: string | null;
  capacity?: number | null;
  feeMember?: number | null;
  feeNonMember?: number | null;
  upiId?: string | null;
  upiQrUrl?: string | null;
  formOptions?: EventFormOptions;
  dropPoints?: EventDropPoint[];
  pickupEnabled?: boolean;
  whatsappReminderEnabled?: boolean;
}

export type EventUpdatePayload = Partial<EventWritePayload>;

export interface EventRegistrationsResponse {
  ok: boolean;
  registrations: EventRegistration[];
  summary: RegistrationSummary;
}

export async function fetchEvents(): Promise<Event[]> {
  const res = await apiFetch<EventsResponse>('/api/events');
  return res.events;
}

export interface EventFlyerChange {
  file: File | null;
  /** Clear the saved flyer or image URL when saving an edit. */
  remove: boolean;
}

export interface EventMediaChange {
  flyer: EventFlyerChange;
  card: EventFlyerChange;
}

function eventWriteInit(
  payload: EventUpdatePayload,
  files: { flyer?: File | null; card?: File | null },
): Pick<RequestInit, 'body'> {
  const flyer = files.flyer ?? null;
  const card = files.card ?? null;
  if (!flyer && !card) return { body: JSON.stringify(payload) };
  const rest: EventUpdatePayload = { ...payload };
  if (flyer) delete rest.image;
  if (card) delete rest.cardImage;
  const form = new FormData();
  form.append('payload', JSON.stringify(rest));
  if (flyer) form.append('image', flyer);
  if (card) form.append('cardImage', card);
  return { body: form };
}

function mediaFiles(media?: EventMediaChange | File | null): { flyer: File | null; card: File | null } {
  if (!media) return { flyer: null, card: null };
  if (media instanceof File) return { flyer: media, card: null };
  return { flyer: media.flyer.file, card: media.card.file };
}

export async function createEvent(
  payload: EventWritePayload,
  media?: EventMediaChange | File | null,
): Promise<Event> {
  const res = await apiFetch<EventResponse>('/api/events', {
    method: 'POST',
    ...eventWriteInit(payload, mediaFiles(media)),
  });
  return res.event;
}

export async function updateEvent(
  id: string,
  payload: EventUpdatePayload,
  media?: EventMediaChange | File | null,
): Promise<Event> {
  const res = await apiFetch<EventResponse>(`/api/events/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    ...eventWriteInit(payload, mediaFiles(media)),
  });
  return res.event;
}

export async function reorderEvent(id: string, direction: 'up' | 'down'): Promise<void> {
  await apiFetch(`/api/events/${encodeURIComponent(id)}/reorder`, {
    method: 'POST',
    body: JSON.stringify({ direction }),
  });
}

export async function deleteEventFlyer(id: string): Promise<Event> {
  const res = await apiFetch<EventResponse>(`/api/events/${encodeURIComponent(id)}/flyer`, {
    method: 'DELETE',
  });
  return res.event;
}

export async function deleteEventCardImage(id: string): Promise<Event> {
  const res = await apiFetch<EventResponse>(`/api/events/${encodeURIComponent(id)}/card-image`, {
    method: 'DELETE',
  });
  return res.event;
}

export async function fetchEventFlyer(eventId: string): Promise<Blob> {
  return fetchBlob(`/api/events/${encodeURIComponent(eventId)}/flyer`);
}

export async function fetchEventCardImage(eventId: string): Promise<Blob> {
  return fetchBlob(`/api/events/${encodeURIComponent(eventId)}/card-image`);
}

export async function deleteEvent(id: string, pin: string): Promise<void> {
  await apiFetch(`/api/events/${encodeURIComponent(id)}`, adminPinDelete(pin));
}

export async function addEventParticipant(eventId: string, memberUid: string): Promise<Event> {
  const res = await apiFetch<EventResponse>(
    `/api/events/${encodeURIComponent(eventId)}/participants`,
    {
      method: 'POST',
      body: JSON.stringify({ memberUid }),
    },
  );
  return res.event;
}

export async function removeEventParticipant(eventId: string, memberUid: string, pin: string): Promise<void> {
  await apiFetch(
    `/api/events/${encodeURIComponent(eventId)}/participants/${encodeURIComponent(memberUid)}`,
    adminPinDelete(pin),
  );
}

export interface QuickRegisterResult {
  registration: {
    id: string;
    regNumber: string;
    eventTitle: string;
    name: string;
    memberType: RegistrationMemberType | null;
    amountDue: number | null;
    paymentStatus: RegistrationPaymentStatus;
    createdAt: string;
  };
  whatsappGroupUrl: string | null;
}

/** Member quick register; identity (name, email, profile phone) comes from the session. */
export async function registerForEvent(eventId: string, form: FormData): Promise<QuickRegisterResult> {
  const res = await apiFetch<{ ok: boolean } & QuickRegisterResult>(
    `/api/events/${encodeURIComponent(eventId)}/register`,
    { method: 'POST', body: form },
  );
  return { registration: res.registration, whatsappGroupUrl: res.whatsappGroupUrl ?? null };
}

export function eventRegistrationsPath(eventId: string): string {
  return `/api/events/${encodeURIComponent(eventId)}/registrations`;
}

export async function updateRegistrationPaymentStatus(
  eventId: string,
  regId: string,
  paymentStatus: RegistrationPaymentStatus,
): Promise<EventRegistration> {
  const res = await apiFetch<{ ok: boolean; registration: EventRegistration }>(
    `${eventRegistrationsPath(eventId)}/${encodeURIComponent(regId)}`,
    { method: 'PATCH', body: JSON.stringify({ paymentStatus }) },
  );
  return res.registration;
}

export async function deleteRegistration(eventId: string, regId: string, pin: string): Promise<void> {
  await apiFetch(`${eventRegistrationsPath(eventId)}/${encodeURIComponent(regId)}`, adminPinDelete(pin));
}

async function fetchBlob(path: string): Promise<Blob> {
  const res = await fetch(apiUrl(path), { credentials: 'include' });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const data = (await res.json()) as { message?: string; error?: string };
      message = data.message || data.error || message;
    } catch {
      // non-JSON error body
    }
    throw new ApiError(res.status, message);
  }
  return res.blob();
}

export async function fetchRegistrationScreenshot(eventId: string, regId: string): Promise<Blob> {
  return fetchBlob(`${eventRegistrationsPath(eventId)}/${encodeURIComponent(regId)}/screenshot`);
}

export interface RegistrationExportQuery {
  q?: string;
  paymentStatus?: string;
  memberType?: string;
  sort?: 'newest' | 'oldest';
}

export interface PickupRequestsResponse {
  ok: boolean;
  requests: EventPickupRequest[];
}

export function eventPickupRequestsPath(eventId: string): string {
  return `/api/events/${encodeURIComponent(eventId)}/pickup-requests`;
}

export async function updatePickupRequestStatus(
  eventId: string,
  requestId: string,
  status: PickupRequestStatus,
): Promise<EventPickupRequest> {
  const res = await apiFetch<{ ok: boolean; request: EventPickupRequest }>(
    `${eventPickupRequestsPath(eventId)}/${encodeURIComponent(requestId)}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
  );
  return res.request;
}

export interface PickupExportQuery {
  q?: string;
  status?: string;
  sort?: 'newest' | 'oldest';
}

export async function downloadPickupRequestsExport(
  eventId: string,
  format: 'csv' | 'xlsx',
  filenameBase: string,
  filters: PickupExportQuery = {},
): Promise<void> {
  const params = new URLSearchParams({ format });
  if (filters.q?.trim()) params.set('q', filters.q.trim());
  if (filters.status) params.set('status', filters.status);
  if (filters.sort) params.set('sort', filters.sort);
  const blob = await fetchBlob(`${eventPickupRequestsPath(eventId)}/export?${params}`);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenameBase}-pickup-requests.${format}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export interface PublicPickupResult {
  name: string;
  createdAt: string;
}

/** Public pickup form. No session cookie; the same endpoint the public site uses. */
export async function submitPublicPickupRequest(
  slug: string,
  fields: { name: string; phone: string; address: string; company?: string; turnstileToken?: string },
): Promise<PublicPickupResult> {
  const res = await fetch(apiUrl(`/api/public/events/${encodeURIComponent(slug)}/pickup-requests`), {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      name: fields.name,
      phone: fields.phone,
      address: fields.address,
      company: fields.company ?? '',
      'cf-turnstile-response': fields.turnstileToken ?? '',
    }),
  });
  let data: { ok?: boolean; error?: string; message?: string; field?: string; pickupRequest?: PublicPickupResult } = {};
  try {
    data = (await res.json()) as typeof data;
  } catch {
    data = {};
  }
  if (!res.ok || data.ok === false) {
    const message = data.message || data.error || res.statusText || 'Could not send the pickup request';
    throw new ApiError(res.status, message, data.error, data.field);
  }
  return data.pickupRequest ?? { name: fields.name, createdAt: new Date().toISOString() };
}

export interface WhatsappReminderLog {
  id: string;
  eventId: string;
  registrationId: string | null;
  phone: string;
  status: 'sent' | 'failed';
  error: string | null;
  interaktMessageId: string | null;
  createdAt: string;
}

export async function fetchWhatsappReminders(eventId: string): Promise<WhatsappReminderLog[]> {
  const data = await apiFetch<{ ok: boolean; reminders: WhatsappReminderLog[] }>(
    `/api/events/${eventId}/whatsapp-reminders`,
  );
  return data.reminders;
}

export async function sendWhatsappReminderTest(eventId: string, phone: string): Promise<WhatsappReminderLog> {
  const data = await apiFetch<{ ok: boolean; reminder: WhatsappReminderLog }>(
    `/api/events/${eventId}/whatsapp-reminders/test`,
    { method: 'POST', body: JSON.stringify({ phone }) },
  );
  return data.reminder;
}

export async function downloadRegistrationsExport(
  eventId: string,
  format: 'csv' | 'xlsx',
  filenameBase: string,
  filters: RegistrationExportQuery = {},
): Promise<void> {
  const params = new URLSearchParams({ format });
  if (filters.q?.trim()) params.set('q', filters.q.trim());
  if (filters.paymentStatus) params.set('paymentStatus', filters.paymentStatus);
  if (filters.memberType) params.set('memberType', filters.memberType);
  if (filters.sort) params.set('sort', filters.sort);
  const blob = await fetchBlob(`${eventRegistrationsPath(eventId)}/export?${params}`);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenameBase}-registrations.${format}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
